import React, { useEffect, useState } from "react";
import { RefreshCw, Siren, AlertTriangle, Clock, CalendarClock, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const STATES = {
  late: { label: "Terlambat — prioritas", icon: Siren, cls: "bg-red-100 text-red-700" },
  urgent: { label: "Segera (<3 jam)", icon: AlertTriangle, cls: "bg-orange-100 text-orange-700" },
  today: { label: "Wajib hari ini", icon: Clock, cls: "bg-amber-100 text-amber-800" },
  upcoming: { label: "Dijadwalkan", icon: CalendarClock, cls: "bg-blue-100 text-blue-700" },
  no_deadline: { label: "Tanpa deadline", icon: HelpCircle, cls: "bg-gray-100 text-gray-600" },
};
const TABS = [
  { v: "action", label: "Perlu Tindakan" },
  { v: "late", label: "Terlambat" },
  { v: "urgent", label: "Segera" },
  { v: "today", label: "Hari Ini" },
  { v: "upcoming", label: "Dijadwalkan" },
  { v: "all", label: "Semua" },
];

export default function PendingShipments() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState("action");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/desty/pending-shipments"); setData(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await api.post("/desty/pending-shipments/refresh");
      toast.success("Sinkronisasi dari Desty dimulai...");
      for (let i = 0; i < 15; i++) {
        await new Promise((res) => setTimeout(res, 4000));
        const r = await api.get("/desty/pending-shipments");
        setData(r.data);
        if (!r.data.scanning) { toast.success("Data paket diperbarui"); break; }
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRefreshing(false); }
  };

  const rows = (data?.rows || []).filter((r) => {
    if (tab === "action" && !["late", "urgent", "today"].includes(r.state)) return false;
    if (!["action", "all"].includes(tab) && r.state !== tab) return false;
    if (q) { const s = `${r.order_sn} ${r.platform} ${r.store_name} ${r.buyer}`.toLowerCase(); if (!s.includes(q.toLowerCase())) return false; }
    return true;
  });
  const sm = data?.summary || {};

  return (
    <div data-testid="pending-shipments-page">
      <PageHeader title="Paket Belum Dikirim" subtitle="Order Desty yang belum diserahkan ke kurir — diingatkan berdasarkan deadline">
        <Button onClick={refresh} disabled={refreshing} className="bg-cocoa text-ivory" data-testid="refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />{refreshing ? "Memperbarui..." : "Refresh dari Desty"}
        </Button>
      </PageHeader>

      <div className="grid gap-3 sm:grid-cols-4 mb-4">
        <Panel className="p-4" data-testid="sum-late"><div className="text-xs text-wmuted">Terlambat</div><div className="text-2xl font-bold text-red-600">{sm.late || 0}</div></Panel>
        <Panel className="p-4" data-testid="sum-urgent"><div className="text-xs text-wmuted">Segera (&lt;3 jam)</div><div className="text-2xl font-bold text-orange-600">{sm.urgent || 0}</div></Panel>
        <Panel className="p-4" data-testid="sum-today"><div className="text-xs text-wmuted">Wajib hari ini</div><div className="text-2xl font-bold text-amber-600">{sm.today || 0}</div></Panel>
        <Panel className="p-4" data-testid="sum-total"><div className="text-xs text-wmuted">Total belum dikirim</div><div className="text-2xl font-bold text-cocoa">{sm.total || 0}</div></Panel>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => (
            <Button key={t.v} size="sm" variant={tab === t.v ? "default" : "outline"} onClick={() => setTab(t.v)} className={tab === t.v ? "bg-cocoa text-ivory" : "border-taupe text-cocoa"} data-testid={`tab-${t.v}`}>{t.label}</Button>
          ))}
        </div>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari No. Order / toko / pembeli" className="bg-white w-64" data-testid="search-input" />
      </div>

      <Panel className="overflow-x-auto">
        {loading ? <div className="py-10 text-center text-wmuted text-sm" data-testid="loading">Memuat...</div> : (
          <Table data-testid="pending-table">
            <TableHeader><TableRow>
              <TableHead>No. Order</TableHead><TableHead>Platform</TableHead><TableHead>Toko</TableHead>
              <TableHead className="text-right">Item</TableHead><TableHead>Deadline</TableHead>
              <TableHead className="text-right">Sisa Waktu</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 ? <TableRow><TableCell colSpan={7} className="py-8 text-center text-wmuted text-sm">Tidak ada paket pada kategori ini 🎉</TableCell></TableRow> :
                rows.map((r) => {
                  const st = STATES[r.state] || STATES.no_deadline; const Icon = st.icon;
                  const hrs = r.hours_left;
                  const htxt = hrs == null ? "-" : hrs < 0 ? `lewat ${Math.abs(hrs)} jam` : `${hrs} jam`;
                  return (
                    <TableRow key={r.order_sn} data-testid={`row-${r.order_sn}`}>
                      <TableCell className="font-mono text-xs">{r.order_sn}</TableCell>
                      <TableCell>{r.platform}</TableCell>
                      <TableCell className="text-xs">{r.store_name || "-"}</TableCell>
                      <TableCell className="text-right">{r.item_count}</TableCell>
                      <TableCell className="text-xs">{r.deadline ? new Date(r.deadline).toLocaleString("id-ID") : "-"}</TableCell>
                      <TableCell className={`text-right text-xs ${r.state === "late" ? "text-red-600 font-semibold" : ""}`}>{htxt}</TableCell>
                      <TableCell><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] ${st.cls}`}><Icon className="w-3 h-3" />{st.label}</span></TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        )}
      </Panel>
      {data?.last_scan_at && <div className="text-xs text-wmuted mt-2" data-testid="last-scan">Terakhir sinkron dari Desty: {new Date(data.last_scan_at).toLocaleString("id-ID")}</div>}
    </div>
  );
}
